/**
 * Formats a date string into a localized format
 * @param {string} dateString - The date string to format
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @returns {string} The formatted date string
 */
export const formatDate = (dateString, locale = 'en-US') => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formats a date string into a short format (DD/MM/YYYY)
 * @param {string} dateString - The date string to format
 * @param {string} separator - The separator to use between date components (default: '/')
 * @returns {string} The formatted date string
 */
export const formatShortDate = (dateString, separator = '/') => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${year}${separator}${month}${separator}${day}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formats a date string to include both date and time
 * @param {string} dateString - The date string to format
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @returns {string} The formatted date and time string
 */
export const formatDateTime = (dateString, locale = 'en-US') => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formats a date string to include both date and time
 * @param {string} dateString - The date string to format
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @returns {string} The formatted date and time string
 */
export const formatTime = (dateString, locale = 'en-US') => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};


export const getMonth = (dateString, group_by) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Formato según el valor de group_by
    switch(group_by) {
      case 'day':
        // Formato: 15 Jul
        return `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' })}`;
      
      case 'week':
        // Obtener el número de semana del año
        const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
        const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
        const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
        return `Week ${weekNumber}`;
      
      case 'month':
      default:
        // Formato: July 2025
        return date.toLocaleString('en-US', {
          month: 'long',
          year: 'numeric'
        });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};